import React, { useMemo, useState, useLayoutEffect } from 'react';
import { Link, NavLink } from 'react-router-dom';

import MenuIcon from '@material-ui/icons/Menu';
import CloseIcon from '@material-ui/icons/Close';
import SearchIcon from '@material-ui/icons/Search';
import PersonOutlineIcon from '@material-ui/icons/PersonOutline';
import ExitToAppIcon from '@material-ui/icons/ExitToApp';
import ShoppingCartOutlinedIcon from '@material-ui/icons/ShoppingCartOutlined';
import HomeOutlinedIcon from '@material-ui/icons/HomeOutlined';

import Autocomplete from '@material-ui/lab/Autocomplete';
import { Paper, TextField } from '@material-ui/core';

import './NavBarD7.scss';
import LogoContainer from 'containers/LogoContainer';

const formatMoney = (n) => {
  const num = Number(n);
  if (!Number.isFinite(num) || num <= 0) return '';
  return `$${num.toLocaleString('de-DE')}`;
};

const getInstallmentsSummary = (cuotasMP, showMPButton) => {
  if (cuotasMP?.error === 'access_token_missing' || showMPButton !== true) return null;
  if (!Array.isArray(cuotasMP)) return null;

  let maxZeroInterest = 0;
  let hasInstallments = false;

  for (const brandData of cuotasMP) {
    if (brandData?.issuers && Array.isArray(brandData.issuers)) {
      for (const issuer of brandData.issuers) {
        if (issuer?.rates && Array.isArray(issuer.rates)) {
          for (const rateOption of issuer.rates) {
            const n = rateOption?.installments;
            const r = Number(rateOption?.rate);
            if (n > 1) hasInstallments = true;
            if (r === 0 && n > maxZeroInterest) maxZeroInterest = n;
          }
        }
      }
    }
  }

  return { maxZeroInterest, hasInstallments };
};

const NavBarD7 = ({
  cartSize,
  cartTotalFormatted,
  categories = [],
  subCategories = [],
  user,
  logout,
  openLoginModal,
  products = [],
  setSelectedProduct,
  selectedProduct,
  onSearchProductChange,
  searchProduct = '',
  onSubmit,
  cuotasMP,
  showMPButton,
}) => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);

  // Keep CSS vars in sync with real fixed header heights (mobile/desktop).
  // This avoids overlays/search being hidden under the header when the logo/font sizes vary.
  useLayoutEffect(() => {
    const setHeaderVars = () => {
      const topbar = document.querySelector('.top-nav-d7');
      const navbar = document.querySelector('.d7-nav');
      const topH = topbar?.offsetHeight || 0;
      const navH = navbar?.offsetHeight || 0;
      const total = topH + navH;
      // Set on <body> so it overrides the fallback vars from SCSS.
      document.body.style.setProperty('--d7-topbar-h', `${topH}px`);
      document.body.style.setProperty('--d7-nav-h', `${navH}px`);
      document.body.style.setProperty('--header-total', `${total}px`);
    };

    // run twice to account for late font/logo load affecting sizes
    setHeaderVars();
    const raf = window.requestAnimationFrame(setHeaderVars);

    window.addEventListener('resize', setHeaderVars);
    return () => {
      window.removeEventListener('resize', setHeaderVars);
      window.cancelAnimationFrame(raf);
    };
  }, []);

  useLayoutEffect(() => {
    if (!drawerOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [drawerOpen]);

  const subsByCatId = useMemo(() => {
    const m = {};
    (subCategories ?? []).forEach((sc) => {
      if (!m[sc.category_id]) m[sc.category_id] = [];
      m[sc.category_id].push(sc);
    });
    return m;
  }, [subCategories]);

  const handleSearchSubmit = () => {
    if (typeof onSubmit === 'function') onSubmit();
    // Mobile: cerrar el input overlay al buscar (UX esperado).
    setMobileSearchOpen(false);
  };

  const installmentsText = useMemo(() => {
    const summary = getInstallmentsSummary(cuotasMP, showMPButton);
    if (!summary) return '';
    if (summary.maxZeroInterest >= 2) return `Hasta ${summary.maxZeroInterest} cuotas sin interes`;
    if (summary.hasInstallments) return 'Con cuotas';
    return '';
  }, [cuotasMP, showMPButton]);

  const PaperComponent = (paperProps) => (
    <Paper {...paperProps} className={`d7-search-paper ${paperProps.className || ''}`}>
      {paperProps.children}
      <button
        type="button"
        className="d7-search-all"
        onMouseDown={(e) => e.preventDefault()} // keep focus on input
        onClick={handleSearchSubmit}
        disabled={!searchProduct?.length}
      >
        VER TODOS LOS RESULTADOS
      </button>
    </Paper>
  );

  const renderSearchOption = (opt) => {
    if (typeof opt === 'string') return <div className="d7-search-opt">{opt}</div>;
    const img = opt?.image1 ?? opt?.image_1 ?? opt?.image ?? '';
    const hasSale = Number(opt?.sale) > 0;
    const price = hasSale ? opt?.sale : opt?.price;
    const old = hasSale ? opt?.price : null;
    return (
      <div className="d7-search-opt">
        {!!img && (
          <div className="d7-search-opt__img">
            <img src={img} alt="" loading="lazy" />
          </div>
        )}
        <div className="d7-search-opt__meta">
          <div className="d7-search-opt__name">{opt?.name || ''}</div>
          <div className="d7-search-opt__price">
            <span className="d7-search-opt__priceNow">{formatMoney(price)}</span>
            {!!installmentsText && (
              <>
                <span className="d7-search-opt__sep" aria-hidden>|</span>
                <span className="d7-search-opt__installments">{installmentsText}</span>
              </>
            )}
            {!!old && <span className="d7-search-opt__priceOld">{formatMoney(old)}</span>}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="navbar-d7">
      <nav className="d7-nav" aria-label="Navegacion principal">
        <div className="d7-left">
          <button
            type="button"
            className="d7-menu-btn"
            aria-label="Abrir menu"
            onClick={() => setDrawerOpen(true)}
          >
            <MenuIcon />
          </button>

          <div className="d7-search">
            <Autocomplete
              key="d7-search"
              options={products}
              freeSolo
              filterOptions={(options) => options}
              PopperProps={{ style: { zIndex: 2300 } }}
              onChange={(event, product) => {
                setSelectedProduct(event, product);
              }}
              value={selectedProduct ?? null}
              onInputChange={onSearchProductChange}
              inputValue={searchProduct}
              getOptionSelected={(option, product) => option?.id === product?.id}
              getOptionLabel={(option) => (typeof option === 'string' ? option : (option?.name ?? ''))}
              renderOption={renderSearchOption}
              noOptionsText="Sin resultados"
              openOnFocus
              blurOnSelect
              disableClearable
              forcePopupIcon={false}
              PaperComponent={PaperComponent}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Buscar..."
                  variant="outlined"
                  size="small"
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); e.stopPropagation(); handleSearchSubmit(); } }}
                  InputProps={{
                    ...params.InputProps,
                    notched: false,
                    endAdornment: (
                      <button
                        type="button"
                        className="d7-search-icon"
                        aria-label="Buscar"
                        onClick={handleSearchSubmit}
                        disabled={!searchProduct?.length}
                      >
                        <SearchIcon />
                      </button>
                    ),
                  }}
                />
              )}
            />
          </div>
        </div>

        <Link to="/" className="d7-logo" aria-label="Inicio">
          <LogoContainer />
        </Link>

        <div className="d7-right">
          {!user ? (
            <button type="button" className="d7-right-btn" onClick={openLoginModal} aria-label="Ingresar">
              <PersonOutlineIcon />
            </button>
          ) : (
            <>
              <button type="button" className="d7-right-btn" onClick={logout} aria-label="Cerrar sesión" title="Cerrar sesión">
                <ExitToAppIcon />
              </button>
              <button type="button" className="header-logout-button d7-logout-link" onClick={logout}>
                Cerrar sesión
              </button>
            </>
          )}

          <Link to="/carrito" className="d7-right-btn d7-cart" aria-label="Carrito">
            <ShoppingCartOutlinedIcon />
            {!!Number(cartSize) && <span className="d7-cart-badge" aria-hidden>{cartSize}</span>}
            {!!Number(cartSize) && <span className="cart-total-pill">{cartTotalFormatted}</span>}
          </Link>
        </div>

        <div className="d7-mobile-icons">
          <button type="button" className="d7-icon" aria-label="Menu" onClick={() => setDrawerOpen(true)}>
            <MenuIcon />
          </button>
          <Link to="/carrito" className="d7-icon d7-cart" aria-label="Carrito">
            <ShoppingCartOutlinedIcon />
            {!!Number(cartSize) && <span className="cart-total-pill">{cartTotalFormatted}</span>}
          </Link>
          {!user ? (
            <button type="button" className="d7-icon" aria-label="Ingresar" onClick={openLoginModal}>
              <PersonOutlineIcon />
            </button>
          ) : (
            <button type="button" className="d7-icon" aria-label="Salir" onClick={logout}>
              <ExitToAppIcon />
            </button>
          )}
        </div>
      </nav>

      {/* Buscador mobile (se abre desde bottom nav) */}
      {mobileSearchOpen && (
        <div className="d7-mobile-search" role="region" aria-label="Buscar">
          <div className="d7-search d7-search--mobile">
            <Autocomplete
              key="d7-search-mobile"
              options={products}
              freeSolo
              filterOptions={(options) => options}
              PopperProps={{ style: { zIndex: 2300 } }}
              onChange={(event, product) => {
                setSelectedProduct(event, product);
                setMobileSearchOpen(false);
              }}
              value={selectedProduct ?? null}
              onInputChange={onSearchProductChange}
              inputValue={searchProduct}
              getOptionSelected={(option, product) => option?.id === product?.id}
              getOptionLabel={(option) => (typeof option === 'string' ? option : (option?.name ?? ''))}
              renderOption={renderSearchOption}
              noOptionsText="Sin resultados"
              openOnFocus
              blurOnSelect
              disableClearable
              forcePopupIcon={false}
              PaperComponent={PaperComponent}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Buscar..."
                  variant="outlined"
                  size="small"
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); e.stopPropagation(); handleSearchSubmit(); } }}
                  InputProps={{
                    ...params.InputProps,
                    notched: false,
                    endAdornment: (
                      <button
                        type="button"
                        className="d7-search-icon"
                        aria-label="Buscar"
                        onClick={handleSearchSubmit}
                        disabled={!searchProduct?.length}
                      >
                        <SearchIcon />
                      </button>
                    ),
                  }}
                />
              )}
            />
          </div>
        </div>
      )}

      {/* Drawer categorias (sin hardcode) */}
      {drawerOpen && <div className="d7-overlay" onClick={() => setDrawerOpen(false)} />}
      <aside className={`d7-drawer${drawerOpen ? ' open' : ''}`} role="dialog" aria-modal="true">
        <button className="d7-close" type="button" onClick={() => setDrawerOpen(false)} aria-label="Cerrar menu">
          <CloseIcon />
        </button>

        <ul className="d7-drawer-list">
          {categories.map((cat) => (
            <li key={cat.id} className="d7-drawer-item">
              <NavLink to={`/productos/${cat.code}`} onClick={() => setDrawerOpen(false)}>
                {cat.name}
              </NavLink>
              {subsByCatId[cat.id]?.length ? (
                <ul className="d7-drawer-subs">
                  {subsByCatId[cat.id].map((sub) => (
                    <li key={sub.id}>
                      <NavLink to={`/productos/${cat.code}/${sub.code}`} onClick={() => setDrawerOpen(false)}>
                        {sub.name}
                      </NavLink>
                    </li>
                  ))}
                </ul>
              ) : null}
            </li>
          ))}
          {user && (
            <li className="d7-drawer-item">
              <button
                type="button"
                className="header-logout-button"
                onClick={() => { setDrawerOpen(false); logout(); }}
              >
                Cerrar sesión
              </button>
            </li>
          )}
        </ul>
      </aside>

      {/* Bottom nav mobile (como referencia) */}
      <nav className="d7-bottomnav" aria-label="Navegacion inferior">
        <Link to="/" className="d7-bottombtn" aria-label="Inicio">
          <HomeOutlinedIcon />
        </Link>
        <button type="button" className="d7-bottombtn" aria-label="Menu" onClick={() => setDrawerOpen(true)}>
          <MenuIcon />
        </button>
        <button
          type="button"
          className="d7-bottombtn"
          aria-label="Buscar"
          onClick={() => setMobileSearchOpen((s) => !s)}
        >
          <SearchIcon />
        </button>
        {!user ? (
          <button type="button" className="d7-bottombtn" aria-label="Cuenta" onClick={openLoginModal}>
            <PersonOutlineIcon />
          </button>
        ) : (
          <button type="button" className="d7-bottombtn" aria-label="Salir" onClick={logout}>
            <ExitToAppIcon />
          </button>
        )}
        <Link to="/carrito" className="d7-bottombtn d7-bottombtn--cart" aria-label="Carrito">
          <ShoppingCartOutlinedIcon />
          {!!Number(cartSize) && <span className="d7-bottombadge" aria-hidden>{cartSize}</span>}
          {!!Number(cartSize) && <span className="cart-total-pill">{cartTotalFormatted}</span>}
        </Link>
      </nav>
    </div>
  );
};

export default NavBarD7;
